import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { mainSiteUrl, articlePages } from '@/mocks/homeData';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function BasetaoSpreadsheetPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        {/* Article Header */}
        <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-6 flex-wrap">
              <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
              <span>/</span>
              <span className="text-foreground-600">BaseTao Spreadsheet Guide</span>
            </nav>

            <span className="inline-block px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium mb-4">
              In-Depth Guide
            </span>
            <h1 className="text-3xl md:text-4xl font-heading text-foreground-950 leading-tight mb-4">
              BaseTao Spreadsheet: The Ultimate Guide to Organizing Your BaseTao Shopping
            </h1>
            <p className="text-foreground-500 text-sm md:text-base leading-relaxed max-w-2xl">
              A comprehensive walkthrough on building and using a BaseTao Spreadsheet to track products, 
              compare shipping costs, monitor warehouse status, and calculate final expenses. 
              This guide covers everything from template setup to advanced cost optimization strategies.
            </p>
            <div className="flex items-center gap-4 mt-6 text-xs text-foreground-400">
              <span>Updated: August 2026</span>
              <span>&middot;</span>
              <span>12 min read</span>
            </div>
          </div>
        </section>

        {/* Article Body */}
        <article className="py-12 md:py-16 bg-background-50">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">
                Why You Need a BaseTao Spreadsheet
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  Shopping through BaseTao can quickly become overwhelming when you are juggling 
                  multiple products across different stores. Each item has its own price, shipping 
                  weight, warehouse arrival timeline, QC status, and final cost. Without a structured 
                  system to manage this information, it is remarkably easy to lose track of purchases, 
                  miscalculate shipping expenses, or overlook items that are ready to ship.
                </p>
                <p>
                  This is precisely where a <strong className="text-foreground-900">BaseTao Spreadsheet</strong> 
                  becomes invaluable. By organizing every product into a clear, searchable table format, 
                  you transform chaotic multi-item shopping into a streamlined, data-driven workflow. 
                  The spreadsheet serves as a single source of truth — one place where you can see 
                  every product, every cost, and every status update at a glance.
                </p>
                <p>
                  The spreadsheet approach is not about overcomplicating your shopping. On the contrary, 
                  it reduces cognitive load by externalizing information. Instead of trying to remember 
                  which items are at the warehouse and which are still in transit, you simply consult 
                  your spreadsheet. This mental offloading frees up bandwidth for what actually matters: 
                  finding great products at great prices.
                </p>
              </div>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">
                Essential Columns for Your BaseTao Spreadsheet
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  A well-designed BaseTao Spreadsheet starts with the right columns. While the exact 
                  structure depends on your shopping habits, the following fields form a solid foundation 
                  that covers the vast majority of use cases:
                </p>
              </div>

              <div className="mt-6 overflow-hidden rounded-xl border border-background-200/70">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-background-100">
                      <th className="text-left px-5 py-3 font-heading text-foreground-900 text-xs">Column</th>
                      <th className="text-left px-5 py-3 font-heading text-foreground-900 text-xs">Purpose</th>
                      <th className="text-left px-5 py-3 font-heading text-foreground-900 text-xs">Example</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/60">
                    {[
                      { col: 'Product Name', purpose: 'Identify each item clearly', example: 'Nike Dunk Low Panda' },
                      { col: 'Store / Link', purpose: 'Source reference for reordering', example: 'Store URL or ID' },
                      { col: 'Unit Price (CNY)', purpose: 'Original purchase price', example: '¥299.00' },
                      { col: 'Quantity', purpose: 'Number of units purchased', example: '1' },
                      { col: 'Est. Weight (g)', purpose: 'For shipping cost estimation', example: '850g' },
                      { col: 'Warehouse Status', purpose: 'Track arrival and storage', example: 'Stored / Pending' },
                      { col: 'QC Result', purpose: 'Quality check outcome', example: 'Passed / Pending' },
                      { col: 'Shipping Method', purpose: 'Selected shipping line', example: 'EMS / GD-EMS' },
                      { col: 'Est. Shipping Cost', purpose: 'Estimated freight charge', example: '$18.50' },
                      { col: 'Final Landed Cost', purpose: 'Total cost to your door', example: '$60.50' },
                    ].map((row, i) => (
                      <tr key={i} className="hover:bg-background-100/50 transition-colors">
                        <td className="px-5 py-3 text-foreground-700 font-medium">{row.col}</td>
                        <td className="px-5 py-3 text-foreground-500">{row.purpose}</td>
                        <td className="px-5 py-3 text-foreground-400 font-mono text-xs">{row.example}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">
                Setting Up Cost Calculation Formulas
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  One of the most powerful features of a BaseTao Spreadsheet is the ability to 
                  automatically calculate your total costs using simple formulas. The final landed 
                  cost for each item can be computed by adding the purchase price, domestic shipping 
                  within China (if applicable), agent service fees, and international shipping.
                </p>
                <p>
                  For spreadsheet programs like Google Sheets or Microsoft Excel, the formula looks 
                  something like: <code className="px-2 py-0.5 bg-background-200/70 rounded text-xs text-foreground-700 font-mono">=SUM(Price, DomesticShipping, ServiceFee, InternationalShipping)</code>. 
                  You can then create a running total row that sums all individual item costs to 
                  give you the grand total for your entire haul.
                </p>
                <p>
                  A particularly useful formula to include is the cost-per-gram calculation. By dividing 
                  the total landed cost by the item weight, you get a metric that helps you compare 
                  the efficiency of different products. Lighter items with higher value tend to have 
                  more favorable cost-per-gram ratios, making them more economical to ship internationally.
                </p>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">
                Warehouse Status Tracking Workflow
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  One area where the BaseTao Spreadsheet truly shines is warehouse status tracking. 
                  As products move through the BaseTao system, they pass through several distinct stages. 
                  Your spreadsheet should reflect this workflow clearly:
                </p>
              </div>

              <div className="mt-6 flex flex-col gap-4">
                {[
                  { step: '1', title: 'Purchase Submitted', desc: 'Item ordered from the seller. Record the order date and expected delivery timeframe to the warehouse.' },
                  { step: '2', title: 'Warehouse Arrival', desc: 'Item arrives at the BaseTao warehouse. Update the arrival date in your spreadsheet and mark status as "Arrived."' },
                  { step: '3', title: 'QC Inspection', desc: 'Quality check photos are taken. Review the images and mark QC status: "Passed," "Issue Found," or "Pending Review."' },
                  { step: '4', title: 'Storage', desc: 'Approved items enter storage. Mark the storage date and confirm the item is ready for consolidation and shipping.' },
                  { step: '5', title: 'Shipping Submitted', desc: 'Item is included in a shipping parcel. Record the shipping method, tracking number, and estimated delivery date.' },
                ].map((s) => (
                  <div key={s.step} className="flex gap-4 p-5 rounded-xl bg-background-100 border border-background-200/60">
                    <div className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="font-heading text-foreground-900 text-sm mb-1">{s.title}</h3>
                      <p className="text-foreground-500 text-sm leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">
                Advanced Tips for Power Users
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  Once you have mastered the basics of your BaseTao Spreadsheet, these advanced 
                  techniques can take your organization to the next level:
                </p>
                <ul className="space-y-3 list-disc pl-5">
                  <li>
                    <strong className="text-foreground-900">Conditional formatting for warehouse status:</strong> Use 
                    color-coding rules to automatically highlight items that are "Ready to Ship" in green, 
                    "Pending" in amber, and "Issue" in red. This gives you an instant visual overview of 
                    your haul without reading every cell.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Shipping method comparison sheet:</strong> Create a separate 
                    tab that pulls weight data from your main sheet and compares estimated costs across 
                    multiple shipping lines. This helps you choose the most economical option for each parcel.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Historical price tracking:</strong> Keep a record of past 
                    purchases with dates and prices. Over time, this builds a valuable reference that helps 
                    you spot price trends and identify the best times to buy specific product categories.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Haul budget dashboard:</strong> Add a summary section at 
                    the top of your spreadsheet showing total spent, total estimated shipping, and total 
                    landed cost. This keeps your budget front and center throughout the shopping process.
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 6 */}
            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-5">
                Common Mistakes to Avoid
              </h2>
              <div className="space-y-4 text-foreground-600 text-sm md:text-base leading-relaxed">
                <p>
                  Even experienced BaseTao users sometimes fall into these spreadsheet traps. 
                  Here is what to watch out for:
                </p>
                <ul className="space-y-3 list-disc pl-5">
                  <li>
                    <strong className="text-foreground-900">Neglecting to update weight estimates:</strong> Seller-listed 
                    weights are often approximate. Always update your spreadsheet with the actual weight 
                    measured at the warehouse to get accurate shipping cost calculations.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Forgetting domestic shipping fees:</strong> Some sellers charge 
                    domestic shipping within China. If these small fees are not recorded, your final 
                    cost calculations will be off. Create a dedicated column for domestic shipping.
                  </li>
                  <li>
                    <strong className="text-foreground-900">Not backing up your spreadsheet:</strong> A spreadsheet is only as 
                    safe as its last save. Use cloud-based tools like Google Sheets for automatic saving 
                    and version history, or regularly export your data as a backup.
                  </li>
                </ul>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="mb-12">
              <h2 className="text-xl md:text-2xl font-heading text-foreground-950 mb-6">
                Frequently Asked Questions
              </h2>
              <div className="space-y-3">
                {[
                  {
                    q: 'Can I use Google Sheets for my BaseTao Spreadsheet?',
                    a: 'Absolutely. Google Sheets is an excellent choice for a BaseTao Spreadsheet. It is free, cloud-based, accessible from any device, and supports real-time collaboration. The built-in formula functions cover all the calculations you need for cost tracking and shipping estimation.',
                  },
                  {
                    q: 'How often should I update my BaseTao Spreadsheet?',
                    a: 'We recommend updating your spreadsheet at each key milestone: when you place an order, when the item arrives at the warehouse, after QC photos are reviewed, and when you submit a shipping parcel. Regular updates ensure your data stays accurate and actionable.',
                  },
                  {
                    q: 'Is there a ready-made BaseTao Spreadsheet template available?',
                    a: 'While we do not host downloadable templates on this website, the column structure outlined in this guide gives you everything you need to build your own. Many users find that building the spreadsheet themselves helps them understand the workflow better and customize it to their specific needs.',
                  },
                ].map((faq, i) => (
                  <details key={i} className="group rounded-xl border border-background-200/70 bg-background-100 overflow-hidden">
                    <summary className="px-5 py-3.5 cursor-pointer flex items-center justify-between gap-4 text-foreground-900 font-medium text-sm">
                      {faq.q}
                      <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center group-open:rotate-180 transition-transform duration-200">
                        <i className="ri-arrow-down-s-line text-foreground-400"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-4 text-sm text-foreground-500 leading-relaxed">
                      {faq.a}
                    </div>
                  </details>
                ))}
              </div>
            </section>

            {/* Internal Links */}
            <section className="border-t border-background-200/60 pt-8">
              <h3 className="font-heading text-base text-foreground-900 mb-4">Continue Reading</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {articlePages.filter(a => a.path !== '/basetao-spreadsheet/').slice(0, 4).map((article) => (
                  <Link
                    key={article.path}
                    to={article.path}
                    className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/60 hover:border-primary-300 transition-all duration-200 group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${article.icon} text-sm text-primary-600`}></i>
                    </div>
                    <div>
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-700 transition-colors">{article.title}</span>
                      <p className="text-xs text-foreground-400 mt-0.5">{article.description}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* CTA */}
            <section className="mt-8 p-6 rounded-xl bg-background-100 border border-background-200/60 text-center">
              <p className="text-foreground-600 text-sm mb-4">
                Ready to put this guide into practice? Head over to the main website and start building your own BaseTao Spreadsheet today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  BaseTao Spreadsheet
                </a>
                <Link
                  to="/"
                  className="px-6 py-2.5 text-foreground-600 text-sm font-medium hover:text-primary-600 transition-colors duration-200 whitespace-nowrap"
                >
                  &larr; Back to Homepage
                </Link>
              </div>
            </section>
          </div>
        </article>
      </main>

      {/* Schema.org Article */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'BaseTao Spreadsheet: The Ultimate Guide to Organizing Your BaseTao Shopping',
            description: 'A comprehensive walkthrough on building and using a BaseTao Spreadsheet to track products, compare shipping costs, monitor warehouse status, and calculate final expenses.',
            dateModified: '2026-08-10',
            datePublished: '2026-08-10',
            author: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
            },
            publisher: {
              '@type': 'Organization',
              name: 'BaseTao Sheet',
              logo: {
                '@type': 'ImageObject',
                url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/0dc5bc4081b05862da3d01553c9c9d93.png',
              },
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://basetaosheet.team/basetao-spreadsheet/',
            },
          }),
        }}
      />

      <Footer />
    </div>
  );
}